import {
  Component,
  ChangeDetectionStrategy,
  signal,
  computed,
  inject,
  input,
  effect,
  output,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { NotificationService } from "./notification";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

export interface WikipediaResult {
  pageid: number;
  title: string;
  snippet: string;
  cleanSnippet: string;
  url: string;
  displayUrl: string;
  wordcount: number;
  timestamp: string;
  thumbnailUrl?: string | null;
  description?: string | null;
  extract?: string | null;
}

@Component({
  selector: "app-wikipedia-search",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatIconModule],
  template: `
    <div class="w-full max-w-5xl mx-auto space-y-4 animate-ios-fade">
      <!-- Google-Style Search Bar -->
      <div class="relative w-full max-w-2xl">
        <div class="flex items-center w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 rounded-full px-4 py-2.5 shadow-sm hover:shadow-md focus-within:shadow-md focus-within:border-slate-400 dark:focus-within:border-slate-500 transition-all duration-200 group">
          <mat-icon class="text-slate-400 dark:text-slate-500 mr-3 text-xl shrink-0 group-focus-within:text-blue-500 transition-colors">search</mat-icon>
          <input
            type="text"
            [ngModel]="searchTerm()"
            (ngModelChange)="onSearchInputChange($event)"
            (keydown.enter)="triggerSearch()"
            placeholder="Search Google..."
            class="w-full bg-transparent text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 text-sm font-normal outline-none border-none p-0"
          />
          @if (searchTerm()) {
            <button
              type="button"
              (click)="clearSearch()"
              class="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shrink-0 ml-1 cursor-pointer"
              title="Clear search"
            >
              <mat-icon class="text-lg">close</mat-icon>
            </button>
          }
          <button
            type="button"
            (click)="triggerSearch()"
            class="p-1.5 text-blue-600 hover:text-blue-700 dark:text-blue-400 rounded-full hover:bg-blue-50 dark:hover:bg-blue-950/50 transition-colors shrink-0 ml-1 cursor-pointer"
            title="Search"
          >
            <mat-icon class="text-xl">arrow_forward</mat-icon>
          </button>
        </div>
      </div>

      <!-- About [number] results text only (NO wikipedia.org button or extra links) -->
      <div class="px-1 py-0.5">
        <p class="text-xs text-slate-500 dark:text-slate-400 font-normal">
          @if (loading()) {
            <span>Searching...</span>
          } @else if (results().length > 0) {
            <span>About {{ results().length }} results (0.24 seconds)</span>
          } @else if (currentQuery()) {
            <span>About 0 results for "{{ currentQuery() }}"</span>
          } @else {
            <span>Type a query to search</span>
          }
        </p>
      </div>

      <!-- Loading Skeleton State -->
      @if (loading()) {
        <div class="space-y-4">
          @for (i of [1, 2, 3, 4]; track i) {
            <div class="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-5 md:p-6 shadow-sm animate-pulse space-y-3">
              <div class="h-5 w-3/4 bg-slate-200 dark:bg-slate-800 rounded"></div>
              <div class="space-y-2 pt-1">
                <div class="h-3.5 w-full bg-slate-200 dark:bg-slate-800 rounded"></div>
                <div class="h-3.5 w-5/6 bg-slate-200 dark:bg-slate-800 rounded"></div>
              </div>
            </div>
          }
        </div>
      }

      <!-- Error State -->
      @if (!loading() && error()) {
        <div class="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-6 text-center space-y-3">
          <div class="w-12 h-12 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center mx-auto">
            <mat-icon class="text-2xl">error_outline</mat-icon>
          </div>
          <h4 class="text-base font-bold text-slate-800 dark:text-slate-100">Unable to Fetch Wikipedia Results</h4>
          <p class="text-xs text-slate-600 dark:text-slate-400 max-w-md mx-auto">{{ error() }}</p>
          <button
            type="button"
            (click)="triggerSearch()"
            class="px-4 py-2 rounded-xl bg-amber-500 text-white text-xs font-bold shadow hover:bg-amber-600 transition-colors inline-flex items-center gap-1.5 cursor-pointer"
          >
            <mat-icon class="text-sm">refresh</mat-icon>
            Retry Search
          </button>
        </div>
      }

      <!-- Google Search-like Clean Card Results List -->
      @if (!loading() && !error() && results().length > 0) {
        <div class="space-y-4">
          @for (item of results(); track item.pageid; let i = $index) {
            <article 
              (click)="toggleCardAccordion(i, $event)"
              (keydown.enter)="toggleCardAccordion(i, $event)"
              tabindex="0"
              class="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800/80 rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-md hover:border-blue-500/40 dark:hover:border-blue-500/40 transition-all duration-300 relative group cursor-pointer outline-none focus:ring-2 focus:ring-blue-500/50"
            >
              <!-- Main Card Content Body -->
              <div class="flex flex-col sm:flex-row items-start justify-between gap-4">
                <div class="flex-1 min-w-0 space-y-1.5">
                  <h3 class="text-lg md:text-xl font-semibold tracking-tight text-blue-600 dark:text-blue-400 group-hover:underline leading-snug">
                    {{ item.title }}
                  </h3>
                  <p class="text-xs md:text-sm text-slate-600 dark:text-slate-300 leading-relaxed font-normal line-clamp-3">
                    {{ item.cleanSnippet }}
                  </p>
                </div>

                @if (item.thumbnailUrl) {
                  <div class="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shrink-0 shadow-sm hidden xs:block">
                    <img
                      [src]="item.thumbnailUrl"
                      [alt]="item.title"
                      class="w-full h-full object-cover"
                      referrerpolicy="no-referrer"
                    />
                  </div>
                }
              </div>

              <!-- Embedded Web Page View (Appears directly when card is clicked) -->
              @if (selectedCardIndex() === i) {
                <div (click)="$event.stopPropagation()" class="mt-4 pt-3 border-t border-slate-200 dark:border-slate-800 animate-ios-fade">
                  <div class="w-full h-[520px] md:h-[620px] rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white shadow-inner relative">
                    <iframe
                      [src]="getSafeUrl(item.url)"
                      class="w-full h-full border-0"
                      [title]="'Wikipedia page: ' + item.title"
                      loading="lazy"
                    ></iframe>
                  </div>
                </div>
              }
            </article>
          }
        </div>
      }

      <!-- Empty State -->
      @if (!loading() && !error() && results().length === 0 && currentQuery()) {
        <div class="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-10 text-center space-y-3">
          <div class="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 flex items-center justify-center mx-auto">
            <mat-icon class="text-3xl">search_off</mat-icon>
          </div>
          <h4 class="text-base font-bold text-slate-800 dark:text-slate-100">No Wikipedia Articles Found</h4>
          <p class="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
            We couldn't find any Wikipedia pages matching "{{ currentQuery() }}". Try adjusting your search term or exploring one of the topics above.
          </p>
        </div>
      }

      <!-- Quick Article Preview Modal -->
      @if (activePreview()) {
        <div class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000] flex items-center justify-center p-4 animate-ios-fade">
          <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
            <!-- Modal Header -->
            <div class="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3 bg-slate-50/50 dark:bg-slate-800/50">
              <div class="flex items-center gap-2.5 min-w-0">
                <img src="https://en.wikipedia.org/static/favicon/wikipedia.ico" alt="Wikipedia" class="w-5 h-5" />
                <div>
                  <h3 class="font-bold text-slate-900 dark:text-white text-base truncate">{{ activePreview()?.title }}</h3>
                  <p class="text-[11px] text-slate-500 dark:text-slate-400 truncate">{{ activePreview()?.displayUrl }}</p>
                </div>
              </div>
              <button
                type="button"
                (click)="closePreview()"
                class="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors shrink-0 cursor-pointer"
              >
                <mat-icon class="text-lg">close</mat-icon>
              </button>
            </div>

            <!-- Modal Content Body -->
            <div class="p-6 overflow-y-auto space-y-4 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              @if (activePreview()?.thumbnailUrl) {
                <div class="w-full max-h-56 rounded-2xl overflow-hidden bg-slate-100 dark:bg-slate-800 mb-4 border border-slate-200 dark:border-slate-700">
                  <img
                    [src]="activePreview()?.thumbnailUrl"
                    [alt]="activePreview()?.title"
                    class="w-full h-full object-cover"
                    referrerpolicy="no-referrer"
                  />
                </div>
              }

              @if (activePreview()?.description) {
                <div class="inline-block px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 font-semibold text-xs border border-blue-200 dark:border-blue-800">
                  {{ activePreview()?.description }}
                </div>
              }

              <p class="text-slate-800 dark:text-slate-200 text-sm md:text-base leading-relaxed">
                {{ activePreview()?.extract || activePreview()?.cleanSnippet }}
              </p>
            </div>

            <!-- Modal Footer -->
            <div class="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50 flex items-center justify-between gap-3">
              <button
                type="button"
                (click)="copyLink(activePreview()?.url || '')"
                class="px-4 py-2 rounded-xl bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <mat-icon class="text-sm">content_copy</mat-icon>
                <span>Copy Link</span>
              </button>

              <a
                [href]="activePreview()?.url"
                target="_blank"
                rel="noopener noreferrer"
                class="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs transition-colors shadow-md shadow-blue-500/20 flex items-center gap-1.5 cursor-pointer"
              >
                <span>Read Full Article on Wikipedia</span>
                <mat-icon class="text-sm">open_in_new</mat-icon>
              </a>
            </div>
          </div>
        </div>
      }
    </div>
  `,
})
export class WikipediaSearchComponent {
  private notificationService = inject(NotificationService);
  private sanitizer = inject(DomSanitizer);

  /**
   * Optional initial query input from parent search view
   */
  queryInput = input<string>("");

  searchTerm = signal<string>("");
  currentQuery = signal<string>("");
  results = signal<WikipediaResult[]>([]);
  loading = signal<boolean>(false);
  error = signal<string | null>(null);
  activePreview = signal<WikipediaResult | null>(null);
  selectedCardIndex = signal<number | null>(null);

  sampleTopics = [
    "Artificial Intelligence",
    "Quantum Computing",
    "Indian Space Research Organisation",
    "Renewable Energy",
    "Genomics",
    "Machine Learning",
  ];

  constructor() {
    // Synchronize initial input signal if provided
    effect(() => {
      const q = this.queryInput();
      if (q && q.trim() && q !== this.currentQuery()) {
        this.searchTerm.set(q);
        this.executeWikipediaSearch(q);
      } else if (!this.currentQuery() && !this.loading()) {
        // Default search topic if empty
        const defaultTopic = "Artificial Intelligence";
        this.searchTerm.set(defaultTopic);
        this.executeWikipediaSearch(defaultTopic);
      }
    });
  }

  onSearchInputChange(val: string) {
    this.searchTerm.set(val);
  }

  clearSearch() {
    this.searchTerm.set("");
  }

  selectTopic(topic: string) {
    this.searchTerm.set(topic);
    this.executeWikipediaSearch(topic);
  }

  triggerSearch() {
    const q = this.searchTerm().trim();
    if (q) {
      this.executeWikipediaSearch(q);
    }
  }

  toggleCardAccordion(index: number, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (this.selectedCardIndex() === index) {
      this.selectedCardIndex.set(null);
    } else {
      this.selectedCardIndex.set(index);
    }
  }

  getSafeUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  async executeWikipediaSearch(queryStr: string) {
    if (!queryStr) return;
    this.loading.set(true);
    this.error.set(null);
    this.selectedCardIndex.set(null);
    this.currentQuery.set(queryStr);

    try {
      const searchApiUrl = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(
        queryStr
      )}&utf8=&format=json&origin=*&srlimit=10`;

      const response = await fetch(searchApiUrl);
      if (!response.ok) {
        throw new Error(`Wikipedia API responded with status ${response.status}`);
      }

      const data = await response.json();
      const rawItems = data?.query?.search || [];

      if (!rawItems || rawItems.length === 0) {
        this.results.set([]);
        this.loading.set(false);
        return;
      }

      // Map raw Wikipedia search items to clean result objects
      const baseResults: WikipediaResult[] = rawItems.map((item: any) => {
        const cleanSnippet = this.stripHtmlTags(item.snippet || "");
        const formattedTitle = item.title;
        const pageSlug = encodeURIComponent(formattedTitle.replace(/\s+/g, "_"));
        const url = `https://en.wikipedia.org/wiki/${pageSlug}`;
        const displayUrl = `https://en.wikipedia.org › wiki › ${formattedTitle.replace(/\s+/g, "_")}`;

        return {
          pageid: item.pageid,
          title: item.title,
          snippet: item.snippet,
          cleanSnippet: cleanSnippet || "Detailed Wikipedia article discussing " + item.title + ".",
          url,
          displayUrl,
          wordcount: item.wordcount || 0,
          timestamp: item.timestamp || "",
        };
      });

      this.results.set(baseResults);

      // Asynchronously fetch thumbnails & summary extracts in parallel to enrich result cards
      this.enrichResultsWithSummaries(baseResults);

    } catch (err: any) {
      console.error("Wikipedia Search API Error:", err);
      this.error.set("Failed to connect to Wikipedia API. Please check your internet connection.");
    } finally {
      this.loading.set(false);
    }
  }

  private async enrichResultsWithSummaries(items: WikipediaResult[]) {
    const enrichedPromises = items.map(async (item) => {
      try {
        const summaryUrl = `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(
          item.title
        )}`;
        const res = await fetch(summaryUrl);
        if (res.ok) {
          const summary = await res.json();
          return {
            ...item,
            thumbnailUrl: summary.thumbnail?.source || null,
            description: summary.description || null,
            extract: summary.extract || item.cleanSnippet,
          };
        }
      } catch {
        // Fallback silently if summary endpoint fails for a single article
      }
      return item;
    });

    const enrichedResults = await Promise.all(enrichedPromises);
    this.results.set(enrichedResults);
  }

  private stripHtmlTags(html: string): string {
    if (!html) return "";
    return html.replace(/<[^>]*>?/gm, "").trim();
  }

  copyLink(url: string) {
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      this.notificationService.success("Wikipedia link copied to clipboard!");
    }).catch(() => {
      this.notificationService.info(`Link: ${url}`);
    });
  }

  openPreview(item: WikipediaResult) {
    this.activePreview.set(item);
  }

  closePreview() {
    this.activePreview.set(null);
  }
}
